import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { HomeTopNav } from "@/components/portal/HomeTopNav";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useVerificationResendCooldown } from "@/hooks/useVerificationResendCooldown";
import {
  isLoginFormValid,
  resolveLoginError,
  toLoginRequest,
  type LoginError,
} from "@/lib/api/login-form";
import { loginUser } from "@/lib/api/login-user";
import { resendVerification } from "@/lib/api/resend-verification";
import { formatVerificationResendCooldownLabel } from "@/lib/api/verification-resend-cooldown";
import {
  getResendVerificationErrorMessage,
  RESEND_VERIFICATION_SUCCESS_MESSAGE,
} from "@/lib/api/verify-email-form";
import {
  getAuthSession,
  getRememberMePreference,
  setAuthSession,
  setRememberMePreference,
} from "@/lib/auth/session";
import {
  getRequestAccessAuthNotice,
  readAuthReturnPath,
  resolvePostAuthRedirect,
} from "@/lib/auth/post-auth-redirect";

export default function LoginPage() {
  usePageTitle("Log in");
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = resolvePostAuthRedirect(readAuthReturnPath(location.state));
  const requestAccessNotice = getRequestAccessAuthNotice(
    readAuthReturnPath(location.state),
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(getRememberMePreference);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const resendCooldown = useVerificationResendCooldown();
  const [resetSuccessMessage] = useState<string | null>(() => {
    const state = location.state as {
      resetSuccess?: string;
      verifySuccess?: string;
    } | null;
    const message =
      state?.resetSuccess?.trim() || state?.verifySuccess?.trim();
    return message || null;
  });

  useEffect(() => {
    const state = location.state as {
      resetSuccess?: string;
      verifySuccess?: string;
    } | null;
    const message =
      state?.resetSuccess?.trim() || state?.verifySuccess?.trim();
    if (!message) {
      return;
    }

    navigate(location.pathname, {
      replace: true,
      state: returnPath !== "/" ? { from: returnPath } : null,
    });
  }, [location.pathname, location.state, navigate, returnPath]);

  if (getAuthSession()) {
    return <Navigate to={returnPath} replace />;
  }

  const canSubmit = isLoginFormValid({ email, password });

  async function handleLogin(event?: { preventDefault(): void }) {
    event?.preventDefault();
    if (!canSubmit) {
      return;
    }

    setLoading(true);
    setError(null);
    setResendNotice(null);
    setResendError(null);
    setRememberMePreference(rememberMe);

    try {
      const response = await loginUser(toLoginRequest({ email, password }));
      setAuthSession(
        {
          accessToken: response.access_token,
          tokenType: response.token_type,
          expiresIn: response.expires_in,
          user: response.user,
        },
        { persist: rememberMe ? "local" : "session" },
      );
      navigate(returnPath);
    } catch (err: unknown) {
      setError(resolveLoginError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    const trimmed = email.trim();
    if (!trimmed) {
      setResendError("Enter your email address first.");
      return;
    }
    if (resendCooldown.isCoolingDown) {
      return;
    }

    setResendLoading(true);
    setResendError(null);
    setResendNotice(null);

    try {
      const response = await resendVerification({ email: trimmed });
      setResendNotice(response.message || RESEND_VERIFICATION_SUCCESS_MESSAGE);
      resendCooldown.start();
    } catch (err: unknown) {
      setResendError(getResendVerificationErrorMessage(err));
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <>
      <HomeTopNav />
      <main id="main-content" className="auth-shell">
        <div style={{ width: "100%", maxWidth: 440 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "1.5rem",
            }}
          >
            <AbcFitnessBrandLogo to="" theme="light" />
            <div style={{ fontWeight: 800, fontSize: 16, color: "var(--t1)", marginTop: ".75rem" }}>
              ABC Fitness Developer Portal
            </div>
          </div>

          <div className="auth-card" data-testid="home-login-card">
            <div
              className="auth-hdr"
              style={{ background: "var(--slate-dark)" }}
            >
              <p className="auth-eye">Welcome back</p>
              <h1 data-testid="home-login-title">Log in to your account</h1>
              <p>Enter your credentials to access the developer portal.</p>
            </div>
            <div className="auth-body">
              {requestAccessNotice ? (
                <div
                  className="notice-msg"
                  data-testid="home-login-request-access-notice"
                  role="status"
                  style={{ marginBottom: "1rem" }}
                >
                  <i className="ti ti-info-circle" />
                  <span>{requestAccessNotice}</span>
                </div>
              ) : null}

              {resetSuccessMessage && (
                <div
                  className="success-msg"
                  data-testid="home-login-success"
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    padding: "12px 14px",
                    borderRadius: 8,
                    background: "rgba(0, 181, 136, 0.08)",
                    color: "var(--t1)",
                    fontSize: 13,
                    marginBottom: "1rem",
                  }}
                >
                  <i className="ti ti-circle-check" style={{ color: "var(--abc)" }} />
                  <span>{resetSuccessMessage}</span>
                </div>
              )}

              <form
                className="auth-form"
                data-testid="home-login-form"
                noValidate
                onSubmit={(event) => {
                  void handleLogin(event);
                }}
              >
              {error &&
                (error.waiting ? (
                  <div
                    className="notice-msg"
                    data-testid="home-login-notice"
                    role="status"
                  >
                    <i className="ti ti-clock-hour-4" />
                    <span>{error.message}</span>
                  </div>
                ) : (
                  <div
                    className="error-msg"
                    data-testid="home-login-error"
                    role="alert"
                  >
                    <i className="ti ti-alert-circle" />
                    <span>
                      {error.message}{" "}
                      {error.needsVerification ? (
                        <button
                          type="button"
                          data-testid="home-login-resend-verification"
                          onClick={handleResendVerification}
                          disabled={resendLoading || resendCooldown.isCoolingDown}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "inherit",
                            textDecoration: "underline",
                            cursor:
                              resendLoading || resendCooldown.isCoolingDown
                                ? "wait"
                                : "pointer",
                            font: "inherit",
                          }}
                        >
                          {resendLoading
                            ? "Sending…"
                            : formatVerificationResendCooldownLabel(
                                resendCooldown.secondsRemaining,
                              )}
                        </button>
                      ) : null}
                    </span>
                  </div>
                ))}

              {resendNotice ? (
                <div
                  className="success-msg"
                  data-testid="home-login-resend-success"
                  role="status"
                  style={{ marginBottom: "1rem" }}
                >
                  <i className="ti ti-circle-check" />
                  <span>{resendNotice}</span>
                </div>
              ) : null}

              {resendError ? (
                <div
                  className="error-msg"
                  data-testid="home-login-resend-error"
                  role="alert"
                  style={{ marginBottom: "1rem" }}
                >
                  <i className="ti ti-alert-circle" />
                  <span>{resendError}</span>
                </div>
              ) : null}

              <div className="field">
                <label htmlFor="home-login-email">Email address</label>
                <input
                  id="home-login-email"
                  type="email"
                  data-testid="home-login-email"
                  placeholder="you@company.com"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                />
              </div>

              <div className="field">
                <label htmlFor="home-login-password">Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    id="home-login-password"
                    type={showPw ? "text" : "password"}
                    data-testid="home-login-password"
                    placeholder="Your password"
                    autoComplete="current-password"
                    style={{ paddingRight: 40 }}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                  />
                  <button
                    className="show-pw-btn"
                    data-testid="home-login-toggle-password"
                    onClick={() => setShowPw((v) => !v)}
                    type="button"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    aria-pressed={showPw}
                  >
                    <i
                      className={`ti ${showPw ? "ti-eye-off" : "ti-eye"}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: -4,
                  gap: 12,
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    color: "var(--t2)",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <input
                    type="checkbox"
                    data-testid="home-login-remember-me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  data-testid="home-login-forgot-password"
                  onClick={() => navigate("/forgot-password")}
                  style={{
                    fontSize: 12,
                    color: "var(--abc)",
                    fontWeight: 600,
                    textDecoration: "none",
                    background: "none",
                    border: "none",
                    padding: 0,
                    fontFamily: "inherit",
                    cursor: "pointer",
                  }}
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                className="submit-btn"
                data-testid="home-login-submit"
                disabled={loading || !canSubmit}
                style={{
                  width: "100%",
                  justifyContent: "center",
                  marginTop: ".25rem",
                  opacity: !canSubmit ? 0.5 : 1,
                }}
              >
                {loading ? (
                  <>
                    <i
                      className="ti ti-loader-2"
                      style={{ animation: "spin 1s linear infinite" }}
                    />{" "}
                    Signing in…
                  </>
                ) : (
                  <>
                    <i className="ti ti-login" /> Log in
                  </>
                )}
              </button>
              </form>
            </div>
          </div>

          <p className="already">
            Don&apos;t have an account?{" "}
            <Link
              to="/signup"
              state={returnPath !== "/" ? { from: returnPath } : undefined}
              data-testid="home-login-signup-link"
            >
              Sign up →
            </Link>
          </p>
        </div>
      </main>
    </>
  );
}
